import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostAnalysis = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchCostData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchCostData = async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/costs`);
      setCostData(response.data);
    } catch (error) {
      console.error('Error fetching cost data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6'];

  // Prepare pie chart data for direct costs
  const directCostPieData = costData?.breakdown?.direct_costs 
    ? Object.entries(costData.breakdown.direct_costs).map(([key, value], index) => ({
        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: value.budget,
        actual: value.actual,
        percentage: value.percentage,
        color: COLORS[index % COLORS.length]
      }))
    : [];

  // Prepare bar chart data for budget vs actual
  const barChartData = costData?.breakdown?.direct_costs
    ? Object.entries(costData.breakdown.direct_costs).map(([key, value]) => ({
        name: key.split('_')[0].substring(0, 6),
        fullName: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        budget: value.budget,
        actual: value.actual,
        variance: value.budget - value.actual
      }))
    : [];

  if (loading && !costData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-green-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="cost-analysis-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('costs.title')}</h1>
            <p className="text-xs text-gray-500">{t('costs.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Budget Summary Card */}
        {costData?.summary && (
          <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium opacity-80 mb-3">{t('costs.budgetSummary')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs opacity-70">{t('costs.totalBudget')}</p>
                  <p className="text-xl font-bold">{formatCurrency(costData.summary.total_budget)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('costs.spentToDate')}</p>
                  <p className="text-xl font-bold">{formatCurrency(costData.summary.spent)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('costs.committed')}</p>
                  <p className="text-lg font-semibold">{formatCurrency(costData.summary.committed)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('home.remaining')}</p>
                  <p className="text-lg font-semibold">{formatCurrency(costData.summary.remaining)}</p>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span>{t('costs.budgetUtilization')}</span>
                  <span>{((costData.summary.spent / costData.summary.total_budget) * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full"
                    style={{ width: `${(costData.summary.spent / costData.summary.total_budget) * 100}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cost Distribution Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('costs.costDistribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={directCostPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {directCostPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 shadow-lg rounded border text-xs">
                            <p className="font-semibold">{data.name}</p>
                            <p>{t('common.budget')}: {formatCurrency(data.value)}</p>
                            <p>{t('common.actual')}: {formatCurrency(data.actual)}</p>
                            <p>{t('progress.weight')}: {data.percentage}%</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-1 mt-2">
              {directCostPieData.slice(0, 6).map((item, index) => (
                <div key={index} className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="truncate">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Budget vs Actual Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('costs.budgetVsActual')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} margin={{ left: 0, right: 10 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 8 }} angle={-45} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 shadow-lg rounded border text-xs">
                            <p className="font-semibold">{data.fullName}</p>
                            <p className="text-blue-600">{t('common.budget')}: {formatCurrency(data.budget)}</p>
                            <p className="text-green-600">{t('common.actual')}: {formatCurrency(data.actual)}</p>
                            <p className={data.variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {t('common.variance')}: {formatCurrency(Math.abs(data.variance))} {data.variance >= 0 ? t('costs.under') : t('costs.over')}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="budget" name={t('common.budget')} fill="#93c5fd" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" name={t('common.actual')} fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Direct Costs Breakdown */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('costs.directCosts')}</h3>
          {costData?.breakdown?.direct_costs && Object.entries(costData.breakdown.direct_costs).map(([key, value], index) => (
            <Card key={index}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-gray-900">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="text-xs text-gray-500">{value.percentage}%</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-blue-50 rounded p-2">
                    <p className="text-xs text-gray-500">{t('common.budget')}</p>
                    <p className="text-xs font-bold text-blue-600">{formatCurrency(value.budget)}</p>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <p className="text-xs text-gray-500">{t('common.actual')}</p>
                    <p className="text-xs font-bold text-green-600">{formatCurrency(value.actual)}</p>
                  </div>
                  <div className={`rounded p-2 ${value.budget - value.actual >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-xs text-gray-500">{t('common.variance')}</p>
                    <p className={`text-xs font-bold ${value.budget - value.actual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(value.budget - value.actual))}
                    </p>
                  </div>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${Math.min((value.actual / value.budget) * 100, 100)}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Indirect Costs Breakdown */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('costs.indirectCosts')}</h3>
          {costData?.breakdown?.indirect_costs && Object.entries(costData.breakdown.indirect_costs).map(([key, value], index) => (
            <Card key={index}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-gray-900">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="text-xs text-gray-500">{value.percentage}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{t('common.budget')}</p>
                    <p className="text-sm font-bold text-blue-600">{formatCurrency(value.budget)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('common.actual')}</p>
                    <p className="text-sm font-bold text-green-600">{formatCurrency(value.actual)}</p>
                  </div>
                  <div className={`px-2 py-1 rounded ${value.budget - value.actual >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className={`text-xs font-bold ${value.budget - value.actual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {value.budget - value.actual >= 0 ? '-' : '+'}{formatCurrency(Math.abs(value.budget - value.actual))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CostAnalysis;
